/**
 * The player directory — every account, with their money in aggregate.
 *
 * This is the screen an operator opens when a player is on the phone ("where
 * is my money?") or a pattern needs eyeballing across accounts. Each row is a
 * roll-up the API computes from the ledger-backed tables; the row links to the
 * per-player detail.
 *
 * Four tabs, not one list: "Winners" (at least one payout, most recent win
 * first) is the landing tab — it's the list an operator actually wants most
 * often now that reward-mode payouts are a deliberate, everyday thing rather
 * than a rare event, and it's a much shorter list to scan than the full
 * directory. "All players" is the previous unfiltered behavior, one tap away.
 * Both of those, and "Banned", exclude phantom test accounts (§4) — they are
 * simulated players, not real ones, and mixing their "wins" and balances
 * into a real-money view is exactly the kind of noise this screen exists to
 * cut through. "Phantom" is the one place they're visible, for an operator
 * who specifically needs to check the phantom roster.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { etb, at } from '../format.ts';
import { href, navigate, type Route } from '../router.ts';
import { Empty, ErrorBanner, Spinner, Status } from '../components/ui.tsx';
import type { AdminPlayerView } from '../api/types.ts';

const PAGE_SIZE = 50;

export function Players({ tab }: { tab?: Extract<Route, { name: 'players' }>['tab'] }) {
  const phantomOnly = tab === 'phantom';
  const bannedOnly = tab === 'banned';
  const winnersOnly = !phantomOnly && tab !== 'all' && tab !== 'banned';
  const [players, setPlayers] = useState<AdminPlayerView[] | null>(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [submittedQ, setSubmittedQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(
    (query: string, start: number, onlyWinners: boolean, onlyBanned: boolean, onlyPhantom: boolean) => {
      setPlayers(null);
      setError(null);
      void api
        .players({
          q: query,
          limit: PAGE_SIZE,
          offset: start,
          winnersOnly: onlyWinners,
          phantom: onlyPhantom,
          ...(onlyBanned ? { status: 'suspended' as const } : {}),
        })
        .then((res) => {
          setPlayers(res.players);
          setTotal(res.total);
        })
        .catch(setError);
    },
    [],
  );

  useEffect(() => {
    load(submittedQ, offset, winnersOnly, bannedOnly, phantomOnly);
  }, [load, submittedQ, offset, winnersOnly, bannedOnly, phantomOnly]);

  // Switching tabs resets paging and the in-progress (unsubmitted) search box
  // — a stale "page 3 of Winners" position makes no sense once landing on
  // "All players", and vice versa.
  useEffect(() => {
    setOffset(0);
    setQ('');
    setSubmittedQ('');
  }, [winnersOnly, bannedOnly, phantomOnly]);

  const search = (): void => {
    setOffset(0);
    setSubmittedQ(q.trim());
  };

  return (
    <section>
      <header className="pagehead">
        <h1>Players</h1>
        {players ? <span className="count">{total}</span> : null}
      </header>

      <div className="tabs">
        <button
          type="button"
          className={`tabs__tab${winnersOnly ? ' tabs__tab--on' : ''}`}
          onClick={() => navigate({ name: 'players' })}
        >
          Winners
        </button>
        <button
          type="button"
          className={`tabs__tab${!winnersOnly && !bannedOnly && !phantomOnly ? ' tabs__tab--on' : ''}`}
          onClick={() => navigate({ name: 'players', tab: 'all' })}
        >
          All players
        </button>
        <button
          type="button"
          className={`tabs__tab${bannedOnly ? ' tabs__tab--on' : ''}`}
          onClick={() => navigate({ name: 'players', tab: 'banned' })}
        >
          Banned
        </button>
        <button
          type="button"
          className={`tabs__tab${phantomOnly ? ' tabs__tab--on' : ''}`}
          onClick={() => navigate({ name: 'players', tab: 'phantom' })}
        >
          Phantom
        </button>
      </div>

      <form
        className="searchbar"
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <input
          className="input"
          placeholder="Name, username, telegram id or referral code"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit" className="button">
          Search
        </button>
      </form>

      <ErrorBanner error={error} />
      {players === null && !error ? <Spinner /> : null}
      {players?.length === 0 ? (
        <Empty>
          {winnersOnly && submittedQ === ''
            ? 'No one has won a round yet.'
            : 'No players match.'}
        </Empty>
      ) : null}

      {players && players.length > 0 ? (
        <>
          <table className="table" role="table">
            <thead role="rowgroup">
              <tr role="row">
                <th scope="col" role="columnheader">Player</th>
                <th scope="col" role="columnheader">Status</th>
                <th scope="col" role="columnheader">Joined</th>
                <th scope="col" role="columnheader" className="num">Available</th>
                <th scope="col" role="columnheader" className="num">Deposited</th>
                <th scope="col" role="columnheader" className="num">Withdrawn</th>
                <th scope="col" role="columnheader" className="num">Winnings</th>
                <th scope="col" role="columnheader" className="num">Wins</th>
                <th scope="col" role="columnheader" className="num">Rounds</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {players.map((p) => (
                <tr role="row" key={p.id}>
                  <td role="cell" data-label="Player">
                    <a className="link" href={href({ name: 'player', id: p.id })}>
                      {p.fullName ?? p.username ?? p.referralCode}
                    </a>
                    {p.username ? <span className="muted"> · @{p.username}</span> : null}
                  </td>
                  <td role="cell" data-label="Status">
                    <Status value={p.status} />
                  </td>
                  <td role="cell" data-label="Joined" className="muted">{at(p.createdAt)}</td>
                  <td role="cell" data-label="Available" className="num">{etb(p.available)}</td>
                  <td role="cell" data-label="Deposited" className="num">{etb(p.totalDeposited)}</td>
                  <td role="cell" data-label="Withdrawn" className="num">{etb(p.totalWithdrawn)}</td>
                  <td role="cell" data-label="Winnings" className="num">{etb(p.totalWinnings)}</td>
                  <td role="cell" data-label="Wins" className="num">{p.wins}</td>
                  <td role="cell" data-label="Rounds" className="num">{p.roundsPlayed}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pager">
            <button
              type="button"
              className="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ‹ Newer
            </button>
            <span className="muted">
              {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
            </span>
            <button
              type="button"
              className="button"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Older ›
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
